import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import {
  LayoutDashboard, Archive, Mail, Send, Trash2, Download, Users
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getAdminKey = () => sessionStorage.getItem('adminKey') || '';
const adminHeaders = () => ({ 'X-Admin-Key': getAdminKey() });

const AdminSubscribersPage = () => {
  const navigate = useNavigate();
  const [subscribers, setSubscribers] = useState([]);
  const [loading, setLoading] = useState(true);

  // Broadcast composer state
  const [subject, setSubject] = useState('');
  const [htmlBody, setHtmlBody] = useState('');
  const [sending, setSending] = useState(false);
  const [confirming, setConfirming] = useState(false);

  useEffect(() => {
    if (!getAdminKey()) {
      navigate('/admin/login');
      return;
    }
    fetchSubscribers();
  }, [navigate]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchSubscribers = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/subscribers`, { headers: adminHeaders() });
      setSubscribers(data.subscribers || []);
    } catch (err) {
      if (err?.response?.status === 401) {
        sessionStorage.removeItem('adminKey');
        navigate('/admin/login');
      } else {
        toast.error('Failed to load subscribers');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleUnsubscribe = async (email) => {
    if (!window.confirm(`Remove ${email} from the list?`)) return;
    try {
      await axios.delete(`${API}/admin/subscribers/${encodeURIComponent(email)}`, { headers: adminHeaders() });
      setSubscribers((prev) => prev.filter((s) => s.email !== email));
      toast.success('Subscriber removed');
    } catch {
      toast.error('Failed to remove subscriber');
    }
  };

  const handleExportCsv = () => {
    if (!subscribers.length) return;
    const header = 'email,source,welcome_code,subscribed_at\n';
    const rows = subscribers
      .map((s) => `${s.email},${s.source || ''},${s.welcome_code || ''},${s.subscribed_at || ''}`)
      .join('\n');
    const blob = new Blob([header + rows], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `ppc-subscribers-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleSendBroadcast = async () => {
    if (!subject.trim() || !htmlBody.trim()) {
      toast.error('Subject and message are required');
      return;
    }
    setSending(true);
    try {
      const { data } = await axios.post(
        `${API}/admin/broadcast`,
        { subject: subject.trim(), html_body: htmlBody.trim() },
        { headers: adminHeaders() }
      );
      toast.success(`Sent to ${data.sent} subscriber${data.sent === 1 ? '' : 's'}${data.failed ? ` (${data.failed} failed)` : ''}`);
      setSubject('');
      setHtmlBody('');
      setConfirming(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to send broadcast');
    } finally {
      setSending(false);
    }
  };

  const handleLogout = () => {
    sessionStorage.removeItem('adminKey');
    navigate('/admin/login');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-bone-white">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-perfect-peach" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-bone-white">
      {/* Admin Top Bar */}
      <div className="bg-[#2D2420] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-14">
          <div className="flex items-center gap-6">
            <span className="font-playfair text-lg font-semibold text-perfect-peach">PPC Admin</span>
            <nav className="hidden sm:flex items-center gap-1">
              <Link to="/admin" className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium text-white/70 hover:bg-white/10 hover:text-white transition-colors">
                <LayoutDashboard className="w-4 h-4" /> Dashboard
              </Link>
              <Link to="/admin/inventory" className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium text-white/70 hover:bg-white/10 hover:text-white transition-colors">
                <Archive className="w-4 h-4" /> Inventory
              </Link>
              <Link to="/admin/subscribers" className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium bg-white/10 text-white" data-testid="admin-nav-subscribers">
                <Mail className="w-4 h-4" /> Subscribers
              </Link>
            </nav>
          </div>
          <div className="flex items-center gap-4">
            <Link to="/" className="text-white/60 hover:text-white text-sm transition-colors">View Store</Link>
            <button onClick={handleLogout} className="text-white/60 hover:text-white text-sm transition-colors">Logout</button>
          </div>
        </div>
      </div>

      {/* Mobile admin nav */}
      <div className="sm:hidden bg-[#2D2420] border-t border-white/10 px-4 pb-2 flex gap-2 overflow-x-auto">
        <Link to="/admin" className="flex-shrink-0 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium text-white/70">
          <LayoutDashboard className="w-4 h-4" /> Dashboard
        </Link>
        <Link to="/admin/inventory" className="flex-shrink-0 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium text-white/70">
          <Archive className="w-4 h-4" /> Inventory
        </Link>
        <Link to="/admin/subscribers" className="flex-shrink-0 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium bg-white/10 text-white">
          <Mail className="w-4 h-4" /> Subscribers
        </Link>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        <h1 className="font-playfair text-2xl sm:text-3xl font-bold text-[#2D2420] mb-6">
          Newsletter
        </h1>

        {/* Stat */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
          <div className="bg-white rounded-2xl p-5 shadow-soft">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-perfect-peach/10 flex items-center justify-center">
                <Users className="w-5 h-5 text-perfect-peach" />
              </div>
              <div>
                <p className="text-african-earth text-xs sm:text-sm">Active Subscribers</p>
                <p className="text-xl font-bold text-[#2D2420]" data-testid="subscribers-count">{subscribers.length}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Broadcast Composer */}
        <div className="bg-white rounded-2xl shadow-soft mb-8 overflow-hidden">
          <div className="p-4 sm:p-6 border-b border-[#E0D5D0] flex items-center justify-between gap-3 flex-wrap">
            <h2 className="font-playfair text-xl sm:text-2xl font-semibold text-[#2D2420]">
              Send Broadcast
            </h2>
            <p className="text-xs text-african-earth">
              Goes to all {subscribers.length} subscriber{subscribers.length === 1 ? '' : 's'}
            </p>
          </div>

          <div className="p-4 sm:p-6 space-y-4">
            <div>
              <label className="block text-xs font-medium text-african-earth uppercase tracking-wide mb-1.5">Subject line</label>
              <input
                type="text"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder="A new ritual just dropped"
                className="w-full px-4 py-2.5 border border-[#E0D5D0] rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-perfect-peach"
                data-testid="broadcast-subject-input"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-african-earth uppercase tracking-wide mb-1.5">
                Message <span className="text-african-earth/60 normal-case">(plain text or basic HTML)</span>
              </label>
              <textarea
                value={htmlBody}
                onChange={(e) => setHtmlBody(e.target.value)}
                rows={10}
                placeholder={'<p>Hi lovely,</p>\n<p>We just launched our new...</p>\n<p><a href="https://theperfectpeachclub.co.za/products">Shop the drop</a></p>'}
                className="w-full px-4 py-2.5 border border-[#E0D5D0] rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-perfect-peach"
                data-testid="broadcast-body-input"
              />
              <p className="text-xs text-african-earth/70 mt-1.5">
                Tip: wrap paragraphs in <code>&lt;p&gt;...&lt;/p&gt;</code> and links as <code>&lt;a href=&quot;...&quot;&gt;...&lt;/a&gt;</code>. Branded header & footer are added automatically.
              </p>
            </div>

            {!confirming ? (
              <button
                onClick={() => setConfirming(true)}
                disabled={!subject.trim() || !htmlBody.trim()}
                className="flex items-center gap-2 bg-perfect-peach text-white px-5 py-2.5 rounded-full text-sm font-medium hover:bg-perfect-peach/90 transition-colors disabled:opacity-50"
                data-testid="broadcast-prepare-btn"
              >
                <Send className="w-4 h-4" /> Prepare to send
              </button>
            ) : (
              <div className="bg-bone-white rounded-xl p-4 border border-perfect-peach/20">
                <p className="text-sm text-[#2D2420] mb-3">
                  About to send to <strong>{subscribers.length}</strong> subscriber{subscribers.length === 1 ? '' : 's'}. This cannot be undone.
                </p>
                <div className="flex gap-2">
                  <button
                    onClick={handleSendBroadcast}
                    disabled={sending}
                    className="flex items-center gap-2 bg-perfect-peach text-white px-5 py-2.5 rounded-full text-sm font-medium hover:bg-perfect-peach/90 transition-colors disabled:opacity-50"
                    data-testid="broadcast-confirm-btn"
                  >
                    <Send className="w-4 h-4" /> {sending ? 'Sending…' : `Send now`}
                  </button>
                  <button
                    onClick={() => setConfirming(false)}
                    disabled={sending}
                    className="px-5 py-2.5 rounded-full text-sm font-medium text-african-earth hover:bg-bone-white border border-[#E0D5D0]"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Subscribers list */}
        <div className="bg-white rounded-2xl shadow-soft overflow-hidden">
          <div className="p-4 sm:p-6 border-b border-[#E0D5D0] flex items-center justify-between gap-3 flex-wrap">
            <h2 className="font-playfair text-xl sm:text-2xl font-semibold text-[#2D2420]">
              Subscribers ({subscribers.length})
            </h2>
            <button
              onClick={handleExportCsv}
              disabled={!subscribers.length}
              className="flex items-center gap-1.5 text-xs sm:text-sm font-medium text-perfect-peach hover:bg-perfect-peach/10 px-3 py-1.5 rounded-full transition-colors disabled:opacity-40"
              data-testid="export-csv-btn"
            >
              <Download className="w-3.5 h-3.5" /> Export CSV
            </button>
          </div>

          {subscribers.length === 0 ? (
            <div className="p-10 text-center text-african-earth">
              <Mail className="w-10 h-10 text-african-earth/30 mx-auto mb-3" />
              <p className="text-sm">No subscribers yet. The "Join the Club" form in your footer will collect them.</p>
            </div>
          ) : (
            <div className="divide-y divide-[#F0EAE6]">
              {subscribers.map((sub) => (
                <div key={sub.email} className="flex items-center justify-between gap-3 px-4 sm:px-6 py-3.5" data-testid={`subscriber-row-${sub.email}`}>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-[#2D2420] truncate">{sub.email}</p>
                    <p className="text-xs text-african-earth mt-0.5">
                      {sub.source ? `via ${sub.source}` : ''}
                      {sub.subscribed_at ? ` · ${new Date(sub.subscribed_at).toLocaleDateString('en-ZA', { day: 'numeric', month: 'short', year: 'numeric' })}` : ''}
                    </p>
                  </div>
                  {sub.welcome_code && (
                    <span className="hidden sm:inline-block text-[10px] font-mono bg-perfect-peach/10 text-perfect-peach px-2 py-1 rounded-full">
                      {sub.welcome_code}
                    </span>
                  )}
                  <button
                    onClick={() => handleUnsubscribe(sub.email)}
                    className="text-african-earth/60 hover:text-red-500 transition-colors p-1.5 rounded-full hover:bg-red-50"
                    aria-label={`Remove ${sub.email}`}
                    data-testid={`unsubscribe-btn-${sub.email}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminSubscribersPage;
